// Step 1 of the Optro work-step import wizard: pick the OpsAudits to import
// from. An Optro instance can hold 100k+ work steps, so the wizard is
// audits-first — the user narrows to the (small) set of active audits being
// tested this cycle, and the next step lists work steps per selected audit.
// Only Active, non-template audits are offered (filtered server-side in
// Optro). Classic page-window pagination, matching Optro's own list tables;
// selection lives in the wizard context so it survives page changes.
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Search } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { TablePaginationBar } from "../../../../components/TablePaginationBar";
import { useOptroImportOpsAudits } from "../../../../hooks/useOptroImportOpsAudits";
import { useOptroConnection } from "../../../../hooks/useOptroConnection";
import { OptroConnectDialog } from "../../controls/optroImport/components/OptroConnectDialog";
import { SelectOpsAuditsTable } from "./components/SelectOpsAuditsTable";
import { useOptroWorkStepImportWizard } from "./optroWorkStepImportWizardContext";

export const OptroWorkStepImportSelectAuditsPage = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  const {
    opsAudits,
    total,
    loading,
    hasLoadedOnce,
    error,
    retry,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
  } = useOptroImportOpsAudits(search);
  const { selectedAudits, toggleAudit, isAuditSelected } =
    useOptroWorkStepImportWizard();

  const { connected: optroConnected, loading: connectionLoading } =
    useOptroConnection();
  const [gateDialogOpen, setGateDialogOpen] = useState(true);

  const initialLoad = loading && !hasLoadedOnce;
  const refetching = loading && hasLoadedOnce;

  if (connectionLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-stone-400" />
      </div>
    );
  }

  if (!optroConnected) {
    return (
      <OptroConnectDialog
        open={gateDialogOpen}
        onOpenChange={(open) => {
          setGateDialogOpen(open);
          if (!open) {
            navigate("/audit/work-steps");
          }
        }}
        onConnected={() => {
          setGateDialogOpen(false);
        }}
      />
    );
  }

  return (
    <div className="h-screen flex flex-col w-full p-6 max-w-[1200px] mx-auto">
      <nav className="text-sm text-stone-500 mb-4">
        <Link to="/audit/work-steps" className="hover:underline">
          Work Steps
        </Link>
        {" › "}
        <span className="text-stone-900">Import from OpsAudit</span>
      </nav>
      <h1 className="text-2xl font-semibold">Select Audits to Import From</h1>
      <p className="text-sm text-stone-500 mt-1 mb-4">
        Choose the audits you are testing this cycle. Midship lists their work
        steps next. Audits stay in Optro, which remains the system of record.
      </p>

      {initialLoad ? (
        <div className="border rounded-lg py-24 text-center">
          <Loader2 className="mx-auto h-6 w-6 animate-spin text-stone-400" />
          <p className="mt-4 font-medium">Loading OpsAudits from Optro...</p>
          <p className="mt-1 text-sm text-stone-500 max-w-md mx-auto">
            Pulling the active audits from the connected Optro instance.
          </p>
        </div>
      ) : error ? (
        <div className="border rounded-lg py-24 text-center">
          <p className="font-medium">Could not load OpsAudits from Optro.</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => {
              setSearch("");
              retry();
            }}
          >
            Retry
          </Button>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3 mb-3 shrink-0">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by audit, ID, category, or lead..."
                className="pl-9"
              />
            </div>
          </div>

          <div className="relative flex-1 min-h-0 border rounded-lg flex flex-col">
            <div
              className={
                refetching
                  ? "flex-1 min-h-0 overflow-y-auto opacity-50 pointer-events-none"
                  : "flex-1 min-h-0 overflow-y-auto"
              }
              aria-busy={refetching}
            >
              <SelectOpsAuditsTable
                opsAudits={opsAudits}
                isSelected={isAuditSelected}
                onToggle={toggleAudit}
              />
            </div>
            <div className="border-t shrink-0">
              <TablePaginationBar
                page={page}
                rowsPerPage={rowsPerPage}
                total={total}
                onPageChange={setPage}
                onRowsPerPageChange={setRowsPerPage}
                loading={loading}
              />
            </div>
            {refetching && (
              <div className="absolute inset-0 z-10 flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-stone-500" />
              </div>
            )}
          </div>

          <div className="shrink-0 flex items-center justify-between pt-4">
            <span className="text-sm text-stone-600">
              {selectedAudits.length === 0
                ? "No OpsAudits selected"
                : `${selectedAudits.length} selected`}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => navigate("/audit/work-steps")}
              >
                Back
              </Button>
              <Button
                disabled={selectedAudits.length === 0}
                onClick={() =>
                  navigate("/audit/work-steps/import/optro/work-steps")
                }
              >
                Continue
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
